import { LOAN_STAGES } from '@/types'
import type { Loan, LoanStage } from '@/types'
import { presentNumber, requestedCommitment } from '@/pages/pipeline-intelligence/helpers'
import { DEFAULT_CURRENCY, currencyOf, sumMoneyByCurrency } from '@/pages/pipeline-intelligence/currency'

const STAGES: LoanStage[] = [...LOAN_STAGES]

/** Approved and heading to close, not yet funded — the Pipeline rail's
 *  Committee and Closing tiles. "stageIndex >= 5" also counted Funded loans. */
export const IN_CLOSING_STAGES: readonly LoanStage[] = ['IC / Committee Approved', 'Rate Lock']

/** Seed-script loans (`scripts/seed_local_demo_deal.py`); never in a deployed
 *  account. Named so a local total says how much of it is illustrative. */
export const ILLUSTRATIVE_LOAN_PREFIX = 'loan-demo-'

export const PRODUCT_NOT_STATED = 'Not stated'

/** Label size on the Pipeline Volume by Stage axis. */
export const STAGE_AXIS_FONT_PX = 10

/**
 * Width of that chart's stage axis: the longest canonical stage name on one
 * line at 0.6em a character. At 72px Recharts wrapped the long names onto
 * three lines and they overprinted each other (CRE-14).
 */
export const STAGE_AXIS_WIDTH = Math.ceil(
  Math.max(...LOAN_STAGES.map((stage) => stage.length)) * STAGE_AXIS_FONT_PX * 0.6,
)

export interface Coverage {
  measured: number
  population: number
}

/** "LTV on 22 of 38 deals" — what an average actually stands on. */
export function coverageLabel(label: string, coverage: Coverage): string {
  return `${label} on ${coverage.measured} of ${coverage.population} deals`
}

/** The deal's stated position in the capital stack ("Senior", "Mezzanine"),
 *  else a product the payload names, else "Not stated". The mix defaulted
 *  every deal to "Senior Mortgage", a stated mezzanine note included. */
export function productOf(loan: Loan): string {
  const position = loan.customFields?.position
  if (typeof position === 'string' && position.trim()) return position.trim()
  if (loan.product) return loan.product
  return PRODUCT_NOT_STATED
}

function ltvPct(ltv: number): number {
  // Same per-row rule as the CEO board: a fraction is put on the percent
  // scale before weighting, never after.
  return ltv <= 1.5 ? ltv * 100 : ltv
}

/**
 * An average of a ratio weighted by loan amount. The weights are amounts, so when
 * the deals it reads are in more than one currency there is no honest weight: the
 * platform carries no FX rate and adds none, and $100M of USD against C$100M of
 * CAD is not equal weight. That average is withheld (null, a dash on screen), never
 * computed by adding the amounts across currencies.
 */
function weighted(
  loans: Loan[],
  value: (loan: Loan) => number | null,
): { average: number | null; coverage: Coverage } {
  let weight = 0
  let total = 0
  let measured = 0
  const currencies = new Set<string>()
  for (const loan of loans) {
    const reading = value(loan)
    if (reading == null) continue
    measured += 1
    currencies.add(currencyOf(loan))
    weight += loan.loanAmount
    total += reading * loan.loanAmount
  }
  return {
    average: weight > 0 && currencies.size <= 1 ? total / weight : null,
    coverage: { measured, population: loans.length },
  }
}

function fundedInYear(loan: Loan, year: number): boolean {
  return loan.stage === 'Funded' && String(loan.fundedAt ?? '').slice(0, 4) === String(year)
}

/** What a deal asks for on the pipeline's one basis — its total commitment,
 *  else its loan amount — so every volume here sums what the grid's Requested
 *  column and the Pipeline header do. `loanAmount` alone holds Calloway's
 *  $68M initial advance of a $71.12M commitment (OVR-19). */
function requested(loan: Loan): number {
  return requestedCommitment(loan) ?? 0
}

/** The currencies a book is held in, the default currency first. */
function currenciesOf(loans: Loan[]): string[] {
  return sumMoneyByCurrency(loans, () => 0).currencies
}

/**
 * Command Center's KPI strip, product mix and stage chart from the live book.
 *
 * Stress test 25 Sep (CRE-08, EXEC-05, CRE-25): "YTD Funded" summed every
 * funded loan ever, "In Closing" counted funded loans (stageIndex >= 5),
 * "Avg Days Open" averaged days in the CURRENT stage, and unknown LTV/DSCR
 * were weighted as zero. Each is fixed here and every average carries its
 * coverage.
 *
 * Money is never added across currencies (a deal's amounts are in its own
 * `currency`, and the platform holds no FX rate): every total is a
 * `MoneyByCurrency`; a weighted ratio over several currencies is withheld; and
 * the two charts, which need one scale, plot the book's lead currency and name
 * the currencies they leave out (`currency.chartExcluded`).
 */
export function deriveMetrics(loans: Loan[], now: Date = new Date()) {
  const year = now.getFullYear()
  const dealCount = loans.length
  const totalPipeline = sumMoneyByCurrency(loans, requestedCommitment)

  const codes = currenciesOf(loans)
  const mixedCurrency = codes.length > 1
  // The currency the charts are drawn in: USD when the book holds any, else the
  // book's one (or first) currency. A mixed book's other currencies are listed,
  // not folded in.
  const chartCurrency = codes.includes(DEFAULT_CURRENCY) ? DEFAULT_CURRENCY : (codes[0] ?? DEFAULT_CURRENCY)
  const chartLoans = mixedCurrency ? loans.filter((l) => currencyOf(l) === chartCurrency) : loans

  // A stored 0 (or a negative) is "not recorded", never a 0% LTV or 0.00x DSCR.
  const ltv = weighted(loans, (l) => {
    const value = presentNumber(l.ltv)
    return value != null && value > 0 ? ltvPct(value) : null
  })
  const dscr = weighted(loans, (l) => {
    const value = presentNumber(l.dscr)
    return value != null && value > 0 ? value : null
  })

  // Closed volume, not a Requested figure: it stays on `loan_amount`, as the
  // CEO board's YTD production sums it.
  const fundedThisYear = loans.filter((l) => fundedInYear(l, year))
  const ytdFunded = sumMoneyByCurrency(fundedThisYear, (l) => l.loanAmount)

  const inClosing = loans.filter((l) => IN_CLOSING_STAGES.includes(l.stage)).length

  // Age since the deal entered the system, not days in its current stage.
  const aged = loans.filter((l) => l.totalDaysOpen != null && l.totalDaysOpen >= 0)
  const avgDaysOpen = aged.length
    ? Math.round(aged.reduce((s, l) => s + (l.totalDaysOpen as number), 0) / aged.length)
    : null

  const stageVolume = STAGES.map((stage) => ({
    stage: stage.replace('Application ', 'App ').replace('Commitment ', 'Cmmt '),
    // Volume is money: the chart currency's only. The count is every deal.
    volume: chartLoans.filter((l) => l.stage === stage).reduce((s, l) => s + requested(l), 0) / 1e6,
    count: loans.filter((l) => l.stage === stage).length,
  }))

  // Grouped case-insensitively: "Senior" and "SENIOR" are one product, not
  // two slices; the first spelling seen names the slice.
  const productMap = new Map<string, { name: string; value: number }>()
  chartLoans.forEach((l) => {
    const p = productOf(l)
    const key = p.trim().toLowerCase()
    const slice = productMap.get(key) ?? { name: p, value: 0 }
    slice.value += requested(l)
    productMap.set(key, slice)
  })
  const productMix = Array.from(productMap.values(), ({ name, value }) => ({
    name,
    value: value / 1e6,
  })).sort((a, b) => b.value - a.value)

  const illustrative = loans.filter((l) => l.id.startsWith(ILLUSTRATIVE_LOAN_PREFIX))

  return {
    dealCount,
    totalPipeline,
    wtdLTV: ltv.average,
    ltvCoverage: ltv.coverage,
    wtdDSCR: dscr.average,
    dscrCoverage: dscr.coverage,
    year,
    ytdFunded,
    ytdFundedCount: fundedThisYear.length,
    inClosing,
    avgDaysOpen,
    ageCoverage: { measured: aged.length, population: dealCount } as Coverage,
    stageVolume,
    productMix,
    illustrativeDeals: illustrative.length,
    illustrativeAmount: sumMoneyByCurrency(illustrative, requestedCommitment),
    currency: {
      /** Every currency the book is held in, the default first. */
      codes,
      /** More than one: there is no single total and none is shown. */
      mixed: mixedCurrency,
      /** The currency `stageVolume` and `productMix` are drawn in. */
      chart: chartCurrency,
      /** Currencies the two charts leave out, named so the omission is visible. */
      chartExcluded: codes.filter((code) => code !== chartCurrency),
    },
  }
}
